import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Input } from '@voilajsx/uikit/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Coins, Loader2, Shield, Plus, Minus, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';

interface LedgerEntry {
  id: string;
  userId: number;
  userName: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
}

interface User {
  id: number;
  name: string | null;
  email: string;
}

const TYPE_LABELS: Record<string, string> = {
  signup_bonus: 'Signup Bonus',
  purchase: 'Purchase',
  download_debit: 'Download',
  admin_grant: 'Admin Grant',
  admin_revoke: 'Admin Revoke',
  refund: 'Refund',
};

const AdminCreditsPage: React.FC = () => {
  const { user, token } = useAuth();

  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Filters
  const [filterUserId, setFilterUserId] = useState('');
  const [filterUserSearch, setFilterUserSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Grant form
  const [grantUserId, setGrantUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantMode, setGrantMode] = useState<'grant' | 'revoke'>('grant');
  const [isSaving, setIsSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';
  const isModeratorManage = user?.role === 'moderator' && user?.level === 'manage';
  const hasAccess = isAdminSystem || isModeratorManage;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchLedger = async (pageNum = page, userId?: string, type?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(pageNum), pageSize: String(pageSize) });
      const uid = userId ?? filterUserId;
      const t = type ?? filterType;
      if (uid) params.set('userId', uid);
      if (t && t !== 'all') params.set('types', t);

      const res = await fetch(
        `${config.api.baseUrl}/api/credit/admin/ledger?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setEntries(data.data?.entries || []);
      setTotal(data.data?.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch credit log');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${config.api.baseUrl}/api/user/admin/list?page=1&pageSize=200`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.data?.users || data.users || data.data || [];
      setUsers(list);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    if (hasAccess && token) {
      fetchLedger(1);
      if (isAdminSystem) fetchUsers();
    }
  }, [token, hasAccess]);

  if (!user) return null;

  const handleGrant = async () => {
    if (!grantUserId || !grantAmount || !grantReason) {
      setError('Please fill in all fields');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const endpoint = grantMode === 'grant' ? '/api/credit/admin/grant' : '/api/credit/admin/revoke';
      const res = await fetch(`${config.api.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId: grantUserId,
          amount: Number(grantAmount),
          reason: grantReason,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const targetUser = users.find((u) => String(u.id) === grantUserId);
      const name = targetUser
        ? targetUser.name || targetUser.email
        : `User #${grantUserId}`;
      setSuccess(
        `${grantMode === 'grant' ? 'Granted' : 'Revoked'} ₹${grantAmount} ${grantMode === 'grant' ? 'to' : 'from'} ${name}`
      );
      setGrantUserId('');
      setGrantAmount('');
      setGrantReason('');
      setUserSearch('');
      fetchLedger(1);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to process request');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUsers = userSearch
    ? users.filter((u) => {
        const searchStr = `${u.name || ''} ${u.email}`.toLowerCase();
        return searchStr.includes(userSearch.toLowerCase());
      })
    : users;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (!hasAccess) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">You don't have permission to view this page.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="Credit Log" description="Manage and view credit transactions" />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Credit Log</h1>
                <p className="text-muted-foreground">Manage user balances and view transaction history</p>
              </div>

              {success && (
                <Alert className="bg-green-950 border-green-800 text-green-300">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}
              {error && (
                <Alert className="bg-destructive/10 border-destructive text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Grant / Revoke Form — admin.system only */}
              {isAdminSystem && (
                <Card className="border-zinc-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Coins className="h-5 w-5" />
                      Adjust Balance
                    </CardTitle>
                    <CardDescription>Grant or revoke balance for a user</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Mode toggle */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => { setGrantMode('grant'); setGrantReason(''); }}
                        style={{
                          padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          border: grantMode === 'grant' ? '1px solid #FFD93D' : '1px solid #444',
                          background: grantMode === 'grant' ? 'rgba(255,217,61,0.1)' : 'transparent',
                          color: grantMode === 'grant' ? '#FFD93D' : '#888',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        <Plus size={14} /> Grant
                      </button>
                      <button
                        onClick={() => { setGrantMode('revoke'); setGrantReason(''); }}
                        style={{
                          padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          border: grantMode === 'revoke' ? '1px solid #ef4444' : '1px solid #444',
                          background: grantMode === 'revoke' ? 'rgba(239,68,68,0.1)' : 'transparent',
                          color: grantMode === 'revoke' ? '#ef4444' : '#888',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        <Minus size={14} /> Revoke
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 sm:col-span-2">
                        <Label>User</Label>
                        <Input
                          placeholder="Search by name or email..."
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            setGrantUserId('');
                          }}
                        />
                        {userSearch && !grantUserId && filteredUsers.length > 0 && (
                          <div style={{
                            border: '1px solid #333', borderRadius: 8, background: '#1a1a1a',
                            maxHeight: 160, overflowY: 'auto',
                          }}>
                            {filteredUsers.slice(0, 8).map((u) => (
                              <div
                                key={u.id}
                                onClick={() => {
                                  setGrantUserId(String(u.id));
                                  setUserSearch(u.name || u.email);
                                }}
                                style={{
                                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                  borderBottom: '1px solid #2a2a2a',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#252520'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <div style={{ fontWeight: 600, color: '#ddd' }}>
                                  {u.name || '(no name)'}
                                </div>
                                <div style={{ fontSize: 11, color: '#888' }}>{u.email} · ID {u.id}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {grantUserId && (
                          <p className="text-xs text-muted-foreground">Selected User ID: {grantUserId}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Amount (₹)</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g., 100"
                          value={grantAmount}
                          onChange={(e) => setGrantAmount(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Reason</Label>
                        <Select value={grantReason} onValueChange={setGrantReason}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {grantMode === 'grant' ? (
                              <>
                                <SelectItem value="Admin grant">Admin grant</SelectItem>
                                <SelectItem value="Complimentary credits">Complimentary credits</SelectItem>
                                <SelectItem value="Bonus credits">Bonus credits</SelectItem>
                                <SelectItem value="Refund for failed download">Refund for failed download</SelectItem>
                                <SelectItem value="Promotional offer">Promotional offer</SelectItem>
                                <SelectItem value="Loyalty reward">Loyalty reward</SelectItem>
                                <SelectItem value="Support resolution">Support resolution</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="Policy violation">Policy violation</SelectItem>
                                <SelectItem value="Duplicate credit entry">Duplicate credit entry</SelectItem>
                                <SelectItem value="Fraudulent activity">Fraudulent activity</SelectItem>
                                <SelectItem value="Account correction">Account correction</SelectItem>
                                <SelectItem value="Refund reversal">Refund reversal</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      onClick={handleGrant}
                      disabled={isSaving || !grantUserId || !grantAmount || !grantReason}
                      className="mt-4 gap-2"
                      style={grantMode === 'revoke' ? { background: '#ef4444', color: '#fff' } : {}}
                    >
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                      {grantMode === 'grant' ? <Plus size={14} /> : <Minus size={14} />}
                      {grantMode === 'grant' ? 'Grant Balance' : 'Revoke Balance'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Credit Log Table */}
              <Card className="border-zinc-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Coins className="h-5 w-5" />
                        Transaction Log
                      </CardTitle>
                      <CardDescription>
                        {filterUserId || filterType !== 'all' ? 'Filtered results' : 'All credit transactions'} · Page {page} of {totalPages} · {total} total
                      </CardDescription>
                    </div>
                    <Button
                      variant={showFilters ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      Filters
                      {(filterUserId || filterType !== 'all') && (
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%', background: '#FFD93D', color: '#111',
                          fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                        }}>!</span>
                      )}
                    </Button>
                  </div>

                  {showFilters && (
                    <div style={{
                      background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 10,
                      padding: 16, marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end'
                    }}>
                      {/* User filter */}
                      <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                        <label className="text-xs font-medium text-zinc-400 mb-1 block">User</label>
                        <div style={{ position: 'relative' }}>
                          <Input
                            placeholder="Search user..."
                            value={filterUserSearch}
                            onChange={(e) => {
                              setFilterUserSearch(e.target.value);
                              if (!e.target.value) setFilterUserId('');
                            }}
                          />
                          {filterUserId && (
                            <button
                              onClick={() => { setFilterUserId(''); setFilterUserSearch(''); }}
                              style={{
                                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 2
                              }}
                            >
                              <X style={{ width: 14, height: 14 }} />
                            </button>
                          )}
                        </div>
                        {filterUserSearch && !filterUserId && (
                          <div style={{
                            border: '1px solid #333', borderRadius: 8, background: '#111',
                            maxHeight: 140, overflowY: 'auto', marginTop: 4, position: 'absolute', zIndex: 10,
                            width: 240,
                          }}>
                            {users
                              .filter((u) => `${u.name || ''} ${u.email}`.toLowerCase().includes(filterUserSearch.toLowerCase()))
                              .slice(0, 6)
                              .map((u) => (
                                <div
                                  key={u.id}
                                  onClick={() => {
                                    setFilterUserId(String(u.id));
                                    setFilterUserSearch(u.name || u.email);
                                  }}
                                  style={{
                                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                    borderBottom: '1px solid #1a1a1a',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#1a1a1a'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <div style={{ fontWeight: 600, color: '#ddd' }}>{u.name || '(no name)'}</div>
                                  <div style={{ fontSize: 11, color: '#888' }}>{u.email}</div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Type filter */}
                      <div style={{ flex: '1 1 160px', minWidth: 150 }}>
                        <label className="text-xs font-medium text-zinc-400 mb-1 block">Type</label>
                        <Select value={filterType} onValueChange={setFilterType}>
                          <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="signup_bonus">Signup Bonus</SelectItem>
                            <SelectItem value="purchase">Purchase</SelectItem>
                            <SelectItem value="download_debit">Download</SelectItem>
                            <SelectItem value="admin_grant">Admin Grant</SelectItem>
                            <SelectItem value="admin_revoke">Admin Revoke</SelectItem>
                            <SelectItem value="refund">Refund</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Buttons */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          size="sm"
                          style={{ background: '#FFD93D', color: '#111' }}
                          onClick={() => { setPage(1); fetchLedger(1, filterUserId, filterType); }}
                        >
                          Apply
                        </Button>
                        {(filterUserId || filterType !== 'all') && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => {
                              setFilterUserId('');
                              setFilterUserSearch('');
                              setFilterType('all');
                              setPage(1);
                              fetchLedger(1, '', 'all');
                            }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-2 py-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-11 rounded-md bg-zinc-800 animate-pulse" />
                      ))}
                    </div>
                  ) : entries.length === 0 ? (
                    <div className="text-center py-12">
                      <Coins className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium mb-2">No transactions found</p>
                    </div>
                  ) : (
                    <div className="rounded-md border border-zinc-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-zinc-700">
                            <TableHead className="border-r border-zinc-700">Date</TableHead>
                            <TableHead className="border-r border-zinc-700">User</TableHead>
                            <TableHead className="border-r border-zinc-700">Type</TableHead>
                            <TableHead className="border-r border-zinc-700 text-right">Amount</TableHead>
                            <TableHead className="border-r border-zinc-700 text-right">Balance After</TableHead>
                            <TableHead>Description</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entries.map((entry) => (
                            <TableRow key={entry.id} className="border-b border-zinc-700">
                              <TableCell className="border-r border-zinc-700 text-sm whitespace-nowrap">
                                {new Date(entry.createdAt).toLocaleString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </TableCell>
                              <TableCell className="border-r border-zinc-700 text-sm">
                                {entry.userName}
                              </TableCell>
                              <TableCell className="border-r border-zinc-700">
                                <Badge
                                  variant={
                                    entry.type === 'admin_grant' || entry.type === 'signup_bonus' || entry.type === 'purchase'
                                      ? 'default'
                                      : entry.type === 'admin_revoke'
                                      ? 'destructive'
                                      : 'secondary'
                                  }
                                >
                                  {TYPE_LABELS[entry.type] || entry.type}
                                </Badge>
                              </TableCell>
                              <TableCell
                                className="border-r border-zinc-700 text-right font-semibold"
                                style={{ color: entry.amount > 0 ? '#4ade80' : '#f87171' }}
                              >
                                {entry.amount > 0 ? '+' : ''}₹{Math.abs(entry.amount)}
                              </TableCell>
                              <TableCell className="border-r border-zinc-700 text-right text-sm">
                                ₹{entry.balanceAfter}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                                {entry.description}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {!isLoading && entries.length > 0 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">Page {page} of {totalPages}</div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => { const p = page - 1; setPage(p); fetchLedger(p); }}
                          disabled={page <= 1}
                          className="gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" /> Previous
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => { const p = page + 1; setPage(p); fetchLedger(p); }}
                          disabled={page >= totalPages}
                          className="gap-1"
                        >
                          Next <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageLayout.Content>
    </PageLayout>
  );
};

export default AdminCreditsPage;
